import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FilaTabla } from 'src/app/compartido/tabla/utilidades/tabla.models';
import { OrdenesService } from '../../utilidades/ordenes.service';
import {
  CONFIGURACION_TABLA_ORDENES,
  DETALLES_ACCIONES_TABLA_ORDENES,
} from './utilidades/tabla-ordenes.static';
import { deepCopy } from 'src/app/compartido/utilidades/objetos.utils';
import { TablaAsincronaComponent } from 'src/app/compartido/tabla-asincrona/tabla-asincrona.component';
import { AlertasService } from 'src/app/nucleo/utilidades/alertas.service';
import {
  ErrorDesarrollo,
  ErrorFrontend,
} from 'src/app/compartido/utilidades/error.utils';
import { SourceTablaOrdenes } from '../../utilidades/source-tabla-ordenes.class';
import { ResponsiveService } from 'src/app/nucleo/utilidades/responsive.service';
import { DialogosService } from 'src/app/nucleo/utilidades/dialogos.service';
import { ConfiguracionDialogoVistaPrevia } from 'src/app/compartido/dialogos/dialogo-vista-previa/utilidades/dialogo-vista-previa.static';
import { FormulariosService } from 'src/app/nucleo/utilidades/formularios.service';
import { Parametros } from 'src/app/compartido/utilidades/compartido.models';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { obtenerFiltro } from 'src/app/compartido/tabla/utilidades/tabla.utils';
import {
  TipoCampoFormulario,
  OpcionCampo,
} from 'src/app/compartido/formulario/utilidades/formulario.models';

@Component({
  templateUrl: './tabla-ordenes.component.html',
  styleUrls: ['./tabla-ordenes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TablaOrdenesComponent implements OnInit, OnDestroy {
  /* Suscripción */
  private readonly _suscripcion = new Subscription();

  constructor(
    private readonly _ordenesService: OrdenesService,
    private readonly _router: Router,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _alertasService: AlertasService,
    private readonly _dialogosService: DialogosService,
    private readonly _formulariosService: FormulariosService,
    private readonly _route: ActivatedRoute,
    protected readonly responsiveService: ResponsiveService,
  ) {
    this.dataSource = new SourceTablaOrdenes(_ordenesService);
  }

  /* Configuración de la tabla */
  protected configTabla = deepCopy(CONFIGURACION_TABLA_ORDENES);

  /* Fuente de datos de la tabla */
  protected dataSource: SourceTablaOrdenes;

  /* Referencia a la tabla */
  @ViewChild('tabla') tabla!: TablaAsincronaComponent;

  /* Si es express */
  private _express = false;

  ngOnInit(): void {
    // Poblar opciones de organización
    this._formulariosService.configurarOpcionesOrganizacion$(
      this.configTabla.filtros!,
      true,
    );

    // Poblar opciones de origen
    this._formulariosService
      .configurarOpcionesOrigen$(this.configTabla.filtros!, true)
      .then((suscrpcion: Subscription) => this._suscripcion.add(suscrpcion));

    // Configurar si es express
    const filtroPrioridad = obtenerFiltro(this.configTabla, 'prioridad')!;
    filtroPrioridad.valorIn = new Subject<string>();
    if (filtroPrioridad.tipo !== TipoCampoFormulario.OPCION_MULTIPLE)
      throw new ErrorDesarrollo(
        'El campo prioridad debe ser de tipo OPCION_MULTIPLE',
      );
    filtroPrioridad.opcionesIn = new Subject<OpcionCampo<string | number>[]>();
    firstValueFrom(this._route.url).then((url) => {
      if (url.some((segment) => segment.path === 'express')) {
        this._express = true;
        filtroPrioridad.opcionesIn!.next([
          { nombre: 'Express (Urgente)', valor: 'express' },
        ]);
        filtroPrioridad.valorIn!.next('express');
        filtroPrioridad.readonly = true;
        this.configTabla.botones = this.configTabla.botones!.filter(
          (boton) => boton.slug !== 'importar',
        );
      }
    });
  }

  ngOnDestroy(): void {
    this._suscripcion.unsubscribe();
  }

  /**
   * Ejecuta una acción en la fila.
   * @param {FilaTabla} fila - La fila seleccionada.
   * @param {string} accion - La acción a ejecutar.
   * @autor Juan Corral
   */
  protected clickAccion(fila: FilaTabla, accion: string): void {
    if (accion === 'etiqueta') {
      this._descargarEtiqueta(fila['id']);
      return;
    }

    const detalles = DETALLES_ACCIONES_TABLA_ORDENES[accion];
    if (detalles === undefined)
      throw new ErrorDesarrollo('Acción no soportada');
    const config: ConfiguracionDialogoVistaPrevia = {
      componente: detalles.componente,
      inputs: { idOrden: fila['id'] },
      output: detalles.output,
      url: this._router.serializeUrl(
        this._router.createUrlTree([...detalles.segmentosUrl, fila['id']]),
      ),
      botonesSuperpuestos: true,
      botonCerrar: true,
    };
    this._dialogosService
      .abrirVistaPrevia$(config, {
        maxWidth: '90vw',
        width: detalles.width + 'px',
      })
      .then(() => this.tabla.cargarPagina());
  }

  /**
   * Descarga las etiqueta de la orden.
   * @param {number} id - El id de la orden.
   * @author Juan Corral
   */
  private _descargarEtiqueta(id: number): void {
    this._dialogosService
      .abrirDialogo$(
        `¿Descargar etiqueta?`,
        `Se generará y descargará un archivo PDF con la etiqueta de la orden.`,
        'Confirmar',
        'Cancelar',
      )
      .then((confirmado: boolean) => {
        if (!confirmado) return;

        this._alertasService.alertar(
          `Generando el PDF, esto puede tardar unos segundos...`,
        );
        const filtros: Parametros =
          id === undefined ? this.tabla.valoresFiltros : { ids: id };
        this._ordenesService
          .descargarEtiquetas$(filtros)
          .then(() =>
            this._alertasService.alertarExito(
              'El archivo ha sido descargado correctamente',
            ),
          )
          .catch((error: ErrorFrontend) =>
            this._alertasService.alertarError('Error: ' + error.message),
          );
      });
  }

  /**
   * Realiza la acción de un botón.
   * @param {string} boton - El botón seleccionado.
   * @autor Juan Corral
   */
  protected clickBoton(boton: string): void {
    if (boton === 'importar') this._router.navigate(['ordenes', 'importar']);
    else if (boton === 'crear' && this._express)
      this._router.navigate(['ordenes', 'express', 'crear']);
    else if (boton === 'crear') this._router.navigate(['ordenes', 'crear']);
    else throw new ErrorDesarrollo('Botón no soportado');
  }
}
